import { useEffect, useState } from 'react'
import { Loader2, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Modal } from '@/components/Modal'

/**
 * Shared edit-details dialog for allocation schedules (depreciation,
 * prepaid, loan). Soft-edit only - description / category / accounts.
 *
 * Math edits (amount / duration / start date) are intentionally NOT
 * exposed here because they would silently invalidate any unposted
 * schedule_entries. The backend allows a math-edit when nothing is
 * posted, but the UX flow is simpler if users void+recreate for those
 * changes - same as how invoices work after `/post`. If a user opens
 * this dialog on a schedule that has posted entries, account-rebinding
 * is still safe; it only affects future posts.
 *
 * Used by:
 *   - pages/fixed-assets/FixedAssetsPage.tsx
 *   - pages/prepaid/PrepaidPage.tsx
 *   - pages/loan-amortization/LoanAmortizationPage.tsx
 */

export interface EditableScheduleSnapshot {
  id: number
  description?: string | null
  category?: string | null
  debit_account?: string | null
  credit_account?: string | null
  third_account?: string | null
  /** Optional override for the dialog's "third account" label. */
  thirdAccountLabel?: string
  /** True when one of the schedule's periods has been posted to GL. */
  hasPostedEntries?: boolean
}

export interface EditSchedulePayload {
  description?: string | null
  category?: string | null
  debit_account?: string | null
  credit_account?: string | null
  third_account?: string | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  schedule: EditableScheduleSnapshot | null
  onSubmit: (id: number, body: EditSchedulePayload) => Promise<void>
  busy?: boolean
  /**
   * Some schedule types use the third-account slot for different roles
   * (loan: interest expense; prepaid: unused). Pass a custom label so
   * the field is named meaningfully on each page.
   */
  thirdAccountLabel?: string
}

export function EditScheduleDialog({ open, onOpenChange, schedule, onSubmit, busy, thirdAccountLabel }: Props) {
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState('')
  const [debitAccount, setDebitAccount] = useState('')
  const [creditAccount, setCreditAccount] = useState('')
  const [thirdAccount, setThirdAccount] = useState('')

  useEffect(() => {
    if (!schedule) return
    setDescription(schedule.description ?? '')
    setCategory(schedule.category ?? '')
    setDebitAccount(schedule.debit_account ?? '')
    setCreditAccount(schedule.credit_account ?? '')
    setThirdAccount(schedule.third_account ?? '')
  }, [schedule])

  const submit = async () => {
    if (!schedule) return
    const body: EditSchedulePayload = {
      description: description.trim() || null,
      category: category.trim() || null,
      debit_account: debitAccount.trim() || null,
      credit_account: creditAccount.trim() || null,
      third_account: thirdAccount.trim() || null,
    }
    await onSubmit(schedule.id, body)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit schedule details"
      description="Update the description, category, or bound account codes. Amount, duration, and start date can only be changed by voiding and recreating the schedule."
      className="max-w-lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={busy || !schedule}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Save changes
          </Button>
        </>
      }
    >
      {schedule ? (
        <div className="space-y-3">
          {schedule.hasPostedEntries ? (
            <Alert>
              <AlertTitle>This schedule has posted entries</AlertTitle>
              <AlertDescription>
                You can update labels and account bindings here; future postings will use the new accounts. To change the amount, duration, or start date, void this schedule and create a new one so the audit trail stays clean.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="es-description">Description</Label>
            <Textarea
              id="es-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Schedule description"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="es-category">Category</Label>
            <Input
              id="es-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="e.g. Office Equipment"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="es-debit">Debit account</Label>
              <Input
                id="es-debit"
                value={debitAccount}
                onChange={(e) => setDebitAccount(e.target.value)}
                placeholder="Account code"
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="es-credit">Credit account</Label>
              <Input
                id="es-credit"
                value={creditAccount}
                onChange={(e) => setCreditAccount(e.target.value)}
                placeholder="Account code"
                className="font-mono"
              />
            </div>
          </div>
          {thirdAccountLabel ? (
            <div className="space-y-1.5">
              <Label htmlFor="es-third">{thirdAccountLabel}</Label>
              <Input
                id="es-third"
                value={thirdAccount}
                onChange={(e) => setThirdAccount(e.target.value)}
                placeholder="Account code"
                className="font-mono"
              />
            </div>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Account codes must already exist in your Chart of Accounts. The Chart of Accounts page refuses to delete an account that is bound here.
          </p>
        </div>
      ) : null}
    </Modal>
  )
}
